/**
 * Global Announcement banner helpers (pure; no imports).
 *
 * The API payload is re-validated here: colors must be strict #rrggbb, the
 * type must be one of the five known types, and the version must be the
 * 16-hex id issued by the backend. Anything else is treated as "no banner".
 */

export const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;
export const VERSION_PATTERN = /^[0-9a-f]{16}$/;
export const DEFAULT_BACKGROUND = "#222222";
export const DEFAULT_TEXT = "#ffffff";
export const DISMISS_KEY_PREFIX = "pp_announcement_dismissed_";
export const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const ANNOUNCEMENT_TYPES = {
  general: { label: "General Announcement", icon: "fas fa-bullhorn", urgent: false },
  maintenance: { label: "Maintenance", icon: "fas fa-wrench", urgent: false },
  service_issue: { label: "Service Issue", icon: "fas fa-exclamation-triangle", urgent: true },
  update: { label: "Update", icon: "fas fa-sync-alt", urgent: false },
  important: { label: "Important Notice", icon: "fas fa-exclamation-circle", urgent: true },
};

export function isValidHex(value) {
  return typeof value === "string" && HEX_PATTERN.test(value);
}

export function safeColor(value, fallback) {
  return isValidHex(value) ? value.toLowerCase() : fallback;
}

/**
 * Normalize the /global_announcement response `data` into a render model,
 * or null when nothing should render.
 */
export function normalizeAnnouncement(data) {
  if (!data || typeof data !== "object" || data.enabled !== true) {
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(ANNOUNCEMENT_TYPES, data.type)) {
    return null;
  }
  const message = typeof data.message === "string" ? data.message.trim() : "";
  if (!message) {
    return null;
  }
  const meta = ANNOUNCEMENT_TYPES[data.type];
  return {
    type: data.type,
    label: meta.label,
    icon: meta.icon,
    urgent: meta.urgent,
    message: message,
    backgroundColor: safeColor(data.background_color, DEFAULT_BACKGROUND),
    textColor: safeColor(data.text_color, DEFAULT_TEXT),
    version:
      typeof data.version === "string" && VERSION_PATTERN.test(data.version)
        ? data.version
        : "",
  };
}

/** Service issues and important notices stay visible until the admin hides them. */
export function isDismissible(announcement) {
  return !!(announcement && !announcement.urgent && announcement.version);
}

export function dismissalKey(version) {
  return VERSION_PATTERN.test(version || "") ? DISMISS_KEY_PREFIX + version : "";
}

export function liveRegionProps(announcement) {
  return announcement && announcement.urgent
    ? { role: "alert", "aria-live": "assertive" }
    : { role: "status", "aria-live": "polite" };
}
